import { Common } from './common';

import {
  EncounterPayment,
  PaymentDashboardQuery,
  PaymentDashboardResponse,
  PaymentInfo,
  PaymentEvent,
  PaymentType,
  Payout,
} from './models/payment';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findOneByEncounterId(encounterId: string): Promise<EncounterPayment> {
    const url = this.common.constructPath(`/encounters/${encounterId}/payment`);
    return this.common.getJSON(url);
  }

  // administrative endpoints
  async createPaymentForEncounter(
    encounterId: string,
    amount: number,
    currency: string,
    previousBalance?: number,
    visitFeeAmount?: number,
    feeType?: string,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/previsit/encounters/payments`);
    return this.common.postJSON(url, {
      encounterId,
      amount,
      currency,
      ...(previousBalance !== undefined && { previousBalance }),
      ...(visitFeeAmount !== undefined && { visitFeeAmount }),
      ...(feeType !== undefined && { feeType }),
    });
  }

  async resolvePaymentForEncounters(
    encounterIds: string[],
  ): Promise<{ captured: string[]; synced: string[]; errored: string[] }> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/resolve`,
    );
    return this.common.postJSON(url, { encounterIds });
  }

  async bulkCancelPaymentsById(
    encounterPaymentIds: string[],
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/cancel`,
    );
    return this.common.postJSON(url, { encounterPaymentIds });
  }

  async bulkRefundPaymentsById(
    encounterPaymentIds: string[],
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/refund`,
    );
    return this.common.postJSON(url, { encounterPaymentIds });
  }

  // patient app endpoints
  async fetchEncounterPaymentInfo(
    authenticationCode: string,
    previsitFormSetId: string,
  ): Promise<PaymentInfo> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/payments/`,
      { previsitFormSetId, authenticationCode },
    );
    return this.common.getJSON(url);
  }

  async refreshPaymentStatus(
    authenticationCode: string,
    previsitFormSetId: string,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/payments/refresh`,
      { previsitFormSetId, authenticationCode },
    );
    return this.common.postJSON(url, {});
  }

  async finishPaymentWithSavedMethod({
    authenticationCode,
    previsitFormSetId,
    methodIndex,
    sendReceipt = false,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    methodIndex: number;
    sendReceipt?: boolean;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/payments/use-saved`,
      { authenticationCode, previsitFormSetId },
    );
    return this.common.postJSON(url, {
      selectedPaymentMethod: methodIndex,
      sendReceipt,
    });
  }

  async updatePaymentReceiptRequest({
    authenticationCode,
    previsitFormSetId,
    sendReceipt,
    email,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    sendReceipt: boolean;
    email?: string;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      '/patient-previsit/encounter/payments/request-receipt',
      { authenticationCode, previsitFormSetId },
    );
    return this.common.postJSON(url, { sendReceipt, email });
  }

  async updatePaymentAmount({
    authenticationCode,
    previsitFormSetId,
    paymentType,
    partialBalanceAmount,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    paymentType: PaymentType;
    partialBalanceAmount: number | null;
  }): Promise<PaymentInfo> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/payments/adjust-payment`,
      { authenticationCode, previsitFormSetId },
    );
    return this.common.postJSON(url, { paymentType, partialBalanceAmount });
  }

  async updatePatientSkipFeedback({
    authenticationCode,
    previsitFormSetId,
    feedback,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    feedback: string;
  }) {
    const url = this.common.constructPath(
      '/patient-previsit/encounter/payments/feedback',
      { authenticationCode, previsitFormSetId },
    );
    return this.common.postJSON(url, { feedback });
  }

  // staff web app endpoints
  async getDashboardPayments(
    query: PaymentDashboardQuery,
  ): Promise<PaymentDashboardResponse> {
    const url = this.common.constructPath(
      '/previsit/encounters/payments/dashboard',
      query,
    );
    return this.common.getJSON(url);
  }

  async downloadPaymentReport(): Promise<void> {
    const url = this.common.constructPath(
      '/previsit/encounters/payments/dashboard/download',
    );
    return this.common.getJSON(url);
  }

  async getPayoutsForAccountIdWithTime(
    accountId: string,
    paidTimestampStart: Date,
    paidTimestampEnd: Date,
  ): Promise<Payout[]> {
    const url = this.common.constructPath('/payouts', {
      accountId,
      paidTimestampStart,
      paidTimestampEnd,
    });
    const { results } = await this.common.getJSON(url);
    return results;
  }

  async getReconReportForPayoutId(
    payoutId: string,
  ): Promise<{ rows: string[][]; header: string[] }> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/payout-reconciliation/${payoutId}`,
      { csv: false },
    );
    return this.common.getJSON(url);
  }

  async getDashboardPaymentEvents(
    encounterPaymentId: string,
  ): Promise<PaymentEvent[]> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/dashboard/${encounterPaymentId}/events`,
    );
    return this.common.getJSON(url);
  }

  async getEncounterPaymentRefundAmount(
    encounterPaymentId: string,
  ): Promise<number> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/dashboard/${encounterPaymentId}/refund-amount`,
    );
    return this.common.getJSON(url);
  }
}
